import { useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useMsal } from "@azure/msal-react";
import type { AccountInfo } from "@azure/msal-browser";
import { b2cConfig, loginScopes, policies } from "../config/msalConfig";
import { signOut, setTokens, IdTokenClaims } from "../store/slices/authSlice";

import {
  clearPolicyMismatch,
  markPolicyMismatch,
  readPolicyMismatch,
} from "../utils/b2cPolicyRecovery";

export { POLICY_MISMATCH_RETRY_KEY } from "../utils/b2cPolicyRecovery";

/**
 * Custom hook for handling Azure AD B2C policy-based authentication flows
 *
 * This hook provides:
 * - Automatic redirect promise handling (Phase 7 requirement)
 * - Password reset flow detection and redirect
 * - Policy validation (ensures signin policy is used)
 * - B2C error handling (AADB2C90118, AADB2C90091)
 * - Token storage in Redux
 * - Manual policy methods (resetPassword, googleLogin, etc.)
 *
 * Error Codes:
 * - AADB2C90118: User forgot password (redirect to reset password policy)
 * - AADB2C90091: User cancelled flow (clear this app's cache and sign in again — never logout)
 */
export function useB2CPolicies() {
  const { instance } = useMsal();
  const dispatch = useDispatch();

  /**
   * Phase 7: Automatic redirect promise handling
   * Handles B2C redirect callbacks and policy-specific flows
   */
  useEffect(() => {
    /**
     * Restart sign-in WITHOUT ending the shared SSO session: clear this app's cached account
     * (or, with none known, this app's local MSAL cache) and start ONE sign-in. If that comes
     * back wrong again, STOP and leave the marker at "stopped" — LoginHandler then shows a
     * manual "Sign in again" instead of auto-logging-in (which would silently round-trip
     * through the same SSO session and loop). See utils/b2cPolicyRecovery.ts.
     */
    const recoverWithOneSignIn = async (account: AccountInfo | null, why: string) => {
      const clearLocal = () => (account ? instance.clearCache({ account }) : instance.clearCache());
      if (readPolicyMismatch()) {
        markPolicyMismatch("stopped");
        console.error(`B2C ${why} persisted after re-login; not redirecting again.`);
        await clearLocal();
        dispatch(signOut());
        return;
      }
      markPolicyMismatch("retrying");
      await clearLocal();
      dispatch(signOut());
      instance
        .loginRedirect({
          authority: import.meta.env.VITE_B2C_CONFIG_INSTANCE,
          scopes: loginScopes,
        })
        .catch((error: unknown) => {
          // The one automatic attempt never left the page (e.g. interaction_in_progress).
          // "retrying" would leave LoginHandler on its loader for good; offer the manual retry.
          console.error(`B2C ${why} re-login could not start:`, error);
          markPolicyMismatch("stopped");
        });
    };

    const handleRedirectPromise = async () => {
      try {
        const response = await instance.handleRedirectPromise();

        if (response) {
          // Store tokens in Redux
          dispatch(
            setTokens({
              accessToken: response.accessToken,
              idTokenClaims: response.idTokenClaims as IdTokenClaims,
            }),
          );

          // Check for password reset flow
          // When user completes password reset, tfp claim will be "resetpassword"
          const tfp = (response.account?.idTokenClaims as any)?.tfp;
          if (tfp?.toLowerCase() === policies.resetpassword.toLowerCase()) {
            // Password was reset, redirect to sign in
            instance.loginRedirect({
              authority: import.meta.env.VITE_B2C_CONFIG_INSTANCE,
              scopes: loginScopes,
            });
            return;
          }

          // Check if account matches signin policy
          // Ensure user logged in with correct policy (not resetpassword or other)
          if (
            response.account &&
            !response.account.homeAccountId.includes(policies.signin.toLowerCase())
          ) {
            // ⚠️ DO NOT logoutRedirect HERE. That hits B2C's end_session endpoint and
            // ends the SSO session SHARED BY EVERY SKYPOINT APP: an account cached by a
            // sibling app under another policy signed the operator out of all of them
            // and flashed a sign-out page before every sign-in. What is wrong is only
            // THIS app's cached account, so only that is cleared (the same rationale as
            // reauthenticate() in authService.ts), then a normal sign-in starts.
            //
            // One automatic attempt per tab: if the account that comes back STILL does
            // not match, STOP and leave the marker at "stopped" — LoginHandler then shows
            // a manual "Sign in again" instead of auto-logging-in (which would silently
            // round-trip through the same SSO session and loop). See b2cPolicyRecovery.ts.
            await recoverWithOneSignIn(response.account, "policy mismatch");
            return;
          }
          // A sign-in on this app's own policy ends any recovery.
          clearPolicyMismatch();
        } else if (readPolicyMismatch() === "retrying") {
          // No redirect result (the visitor came back without finishing the attempt):
          // the in-flight marker is stale, so let LoginHandler sign in normally.
          clearPolicyMismatch();
        }
      } catch (error: unknown) {
        console.error("B2C Policy Error:", error);

        // Handle AADB2C90118: User forgot password
        // User clicked "Forgot password?" link on B2C signin page
        const b2cError = error as { errorMessage?: string };
        if (b2cError.errorMessage && b2cError.errorMessage.indexOf("AADB2C90118") > -1) {
          instance.loginRedirect({
            authority: import.meta.env.VITE_B2C_CONFIG_INSTANCE.replace(
              policies.signin,
              policies.resetpassword,
            ),
            scopes: loginScopes,
          });
        }

        // Handle AADB2C90091: User cancelled flow
        // User clicked "Cancel" on B2C page (signup/reset password).
        // ⚠️ Same rule as the policy mismatch (ruling D3): NO logoutRedirect — that ends the SSO
        // session shared by every SkyPoint app just to restart a sign-in. Clear only this app's
        // cached account and go through the same one-automatic-sign-in recovery.
        if (b2cError.errorMessage && b2cError.errorMessage.indexOf("AADB2C90091") > -1) {
          const cached = instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null;
          await recoverWithOneSignIn(cached, "cancelled B2C flow");
        }
      }
    };

    handleRedirectPromise();
  }, [instance, dispatch]);

  /**
   * Check if authentication interaction is in progress
   */
  const loginInProgress = useCallback((): boolean => {
    return (
      sessionStorage.getItem(`msal.${b2cConfig.clientId}.interaction.status`) ===
      "interaction_in_progress"
    );
  }, []);

  /**
   * Initiate password reset flow
   * Redirects to B2C password reset policy
   */
  const resetPassword = useCallback(async (): Promise<void> => {
    if (loginInProgress()) {
      console.warn("Authentication interaction already in progress");
      return;
    }

    try {
      const resetPasswordAuthority = b2cConfig.instance.replace(
        b2cConfig.policies.signin,
        b2cConfig.policies.resetpassword,
      );

      await instance.loginRedirect({
        authority: resetPasswordAuthority,
        scopes: loginScopes,
      });
    } catch (error) {
      console.error("Password reset failed:", error);
      throw error;
    }
  }, [instance, loginInProgress]);

  /**
   * Handle B2C error codes
   * Returns true if error was handled, false otherwise
   *
   * @param error Error object from MSAL
   * @returns Promise<boolean> indicating if error was handled
   */
  const handleB2CError = useCallback(
    async (error: unknown): Promise<boolean> => {
      const b2cErr = error as { errorMessage?: string; message?: string };
      const errorMessage = b2cErr?.errorMessage || b2cErr?.message || "";

      // AADB2C90091: User cancelled the flow
      if (errorMessage.indexOf("AADB2C90091") > -1) {
        console.log("User cancelled authentication flow");

        if (!loginInProgress()) {
          // Restart login flow
          await instance.loginRedirect({ scopes: loginScopes });
          return true;
        }
      }

      // AADB2C90118: User forgot password
      if (errorMessage.indexOf("AADB2C90118") > -1) {
        console.log("User requested password reset");
        await resetPassword();
        return true;
      }

      return false;
    },
    [instance, loginInProgress, resetPassword],
  );

  /**
   * Google login with new tenant policy
   * @param planId Plan ID for signup
   * @param token Token for signup flow
   */
  const googleLogin = useCallback(
    async (planId: string, token: string): Promise<void> => {
      const newTenantAuthority = b2cConfig.instance.replace(
        b2cConfig.policies.signin,
        b2cConfig.policies.newtenant,
      );

      await instance.loginRedirect({
        authority: newTenantAuthority,
        scopes: loginScopes,
        extraQueryParameters: {
          domain_hint: "google.com",
          planId: planId,
          token: token,
        },
      });
    },
    [instance],
  );

  /**
   * Azure AD login with new tenant policy
   * @param planId Plan ID for signup
   * @param token Token for signup flow
   */
  const azureLogin = useCallback(
    async (planId: string, token: string): Promise<void> => {
      const newTenantAuthority = b2cConfig.instance.replace(
        b2cConfig.policies.signin,
        b2cConfig.policies.newtenant,
      );

      await instance.loginRedirect({
        authority: newTenantAuthority,
        scopes: loginScopes,
        extraQueryParameters: {
          domain_hint: "commonaad",
          planId: planId,
          token: token,
        },
      });
    },
    [instance],
  );

  /**
   * Google signup (clears session and initiates Google login)
   * @param planId Plan ID for signup
   * @param token Token for signup flow
   */
  const googleSignup = useCallback(
    async (planId: string, token: string): Promise<void> => {
      sessionStorage.clear();
      await googleLogin(planId, token);
    },
    [googleLogin],
  );

  /**
   * Azure signup (clears session and initiates Azure login)
   * @param planId Plan ID for signup
   * @param token Token for signup flow
   */
  const azureSignup = useCallback(
    async (planId: string, token: string): Promise<void> => {
      sessionStorage.clear();
      await azureLogin(planId, token);
    },
    [azureLogin],
  );

  /**
   * Validate if account belongs to correct policy
   * @param homeAccountId Account's home account ID
   * @returns boolean indicating if account is valid for signin policy
   */
  const isValidSignInAccount = useCallback((homeAccountId: string): boolean => {
    return homeAccountId.toLowerCase().includes(b2cConfig.policies.signin.toLowerCase());
  }, []);

  return {
    // Policy-based login methods
    resetPassword,
    googleLogin,
    azureLogin,
    googleSignup,
    azureSignup,

    // Error handling
    handleB2CError,

    // Validation
    isValidSignInAccount,

    // State
    loginInProgress: loginInProgress(),

    // Config access (for advanced use cases)
    policies: b2cConfig.policies,
  };
}
